import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AccessService } from '../../../services/access.service';
import { AuthService, UserRoles } from '../../../services/auth.service';
import { BucketService, IBucket } from '../../../services/bucket.service';
import { User, UserService } from '../../../services/user.service';

interface KbAccessRow {
  user: User;
  hasAccess: boolean;
  isOwner: boolean;
  isCreator: boolean;
  busy: boolean;
}

@Component({
  selector: 'app-bucket-dialog',
  templateUrl: './bucket-dialog.component.html',
  styleUrl: './bucket-dialog.component.scss',
})
export class BucketDialogComponent {
  @Input() data: IBucket | undefined = undefined;
  @Input() onComplete = (bucket: IBucket) => {};

  form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    description: new FormControl<string>(''),
  });

  // ─── Access section state ──────────────────────────────────────────
  // Only shown when editing an existing bucket (a grant needs a bucket id) and
  // only to admin / manager / super_admin (the backend rejects the grant
  // endpoints for everyone else).
  accessRows: KbAccessRow[] = [];
  accessLoading = false;
  accessSearch = '';

  // Smaller default than the full-page agent tab — this is a modal, so a page
  // of 25 would make it taller than the viewport.
  accessPage = 0;
  accessPageSize = 10;
  readonly accessPageSizeOptions = [10, 25, 50];

  constructor(
    protected dialogRef: NbDialogRef<BucketDialogComponent>,
    protected filesService: BucketService,
    private accessService: AccessService,
    private userService: UserService,
    private authService: AuthService,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.form.setValue({
        name: this.data.name,
        description: this.data?.description,
      });
      if (this.canManageAccess) {
        this.loadAccess();
      }
    }
  }

  get canManageAccess(): boolean {
    const role = this.authService.accessTokenPayload$.value?.role;
    return (
      !!this.data &&
      (role === UserRoles.Admin ||
        role === UserRoles.Manager ||
        role === UserRoles.SuperAdmin)
    );
  }

  get filteredAccessRows(): KbAccessRow[] {
    const q = this.accessSearch.trim().toLowerCase();
    if (!q) return this.accessRows;
    return this.accessRows.filter(
      (r) =>
        r.user.name?.toLowerCase().includes(q) ||
        r.user.email?.toLowerCase().includes(q),
    );
  }

  get pagedAccessRows(): KbAccessRow[] {
    const start = this.accessPage * this.accessPageSize;
    return this.filteredAccessRows.slice(start, start + this.accessPageSize);
  }

  onAccessSearchChange(): void {
    this.accessPage = 0;
  }

  onAccessPageChange(page: number): void {
    this.accessPage = page;
  }

  onAccessPageSizeChange(size: number): void {
    this.accessPageSize = size;
    this.accessPage = 0;
  }

  private async loadAccess(): Promise<void> {
    if (!this.data) return;
    this.accessLoading = true;
    try {
      const [users, access] = await Promise.all([
        firstValueFrom(this.userService.listUsers()),
        this.accessService.getBucketAccess(this.data.id),
      ]);
      const granted = new Set(access.user_ids);
      this.accessRows = users
        .map((user) => ({
          user,
          hasAccess: granted.has(user.id),
          isOwner: access.owner_user_id === user.id,
          isCreator: access.created_by_user_id === user.id,
          busy: false,
        }))
        .sort(
          (a, b) =>
            Number(b.hasAccess || b.isOwner) - Number(a.hasAccess || a.isOwner) ||
            (a.user.name || '').localeCompare(b.user.name || ''),
        );
    } catch {
      this.toastr.danger(
        this.t.instant('pages.tutors.access.loadError'),
        this.t.instant('toasts.error'),
      );
    } finally {
      this.accessLoading = false;
    }
  }

  async toggleAccess(row: KbAccessRow): Promise<void> {
    if (!this.data || row.busy || row.isOwner) return;
    const next = !row.hasAccess;
    row.busy = true;
    row.hasAccess = next;
    try {
      if (next) {
        await this.accessService.grantKb(row.user.id, this.data.id);
      } else {
        await this.accessService.revokeKb(row.user.id, this.data.id);
      }
    } catch {
      row.hasAccess = !next;
      this.toastr.danger(
        this.t.instant('pages.tutors.access.saveError'),
        this.t.instant('toasts.error'),
      );
    } finally {
      row.busy = false;
    }
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.data) {
      const bucket = await this.filesService.updateBucket({
        ...this.data,
        ...(this.form.value as any),
      });
      this.onComplete(bucket);
    } else {
      const bucket = await this.filesService.createBucket({
        ...(this.form.value as any),
      });
      this.onComplete(bucket);
    }

    this.dialogRef.close();
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }
}
